import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';
import { UploadForm } from '../common/UploadForm';
import * as ActionTypes from '../../../../constants/ActionTypes';
import { saveThemeFile as saveThemeFileAction } from '../../../../actions/theme';
import GenerateSchema from 'generate-schema';
import { themeSystemDefault, sbemShadCNThemeDefault } from '../../../../constants/SystemDefaults';
import { getUpgradedJsonSchema } from 'components/pages/manage/utils';
import { filterThemeUniqueGlobal } from '../../../../constants/uniqueKeyToGlobalMap';
import { OnLeaveSaveFileDialog } from '../../../modals/editFile/OnLeaveSaveFileDialog';
import SaveAndCancelBottomButtons from '../common/saveAndCancelBottomButtons/saveAndCancelBottomButtons';
import { MetaData } from 'components/pages/manage/common/MetaData';
import JsonCodeEditorPanel from 'components/widgets/CodeEditor/JsonCodeEditorPanel';
import { SchemaValidationErrorMessage } from 'components/pages/manage/common/SchemaValidationErrorMessage';
import { THEME_CATEGORY } from '../../../../constants/ThemeCategories';

const createThemeSchema = (isThemeSbem, currentTheme) => {
    const sysDefaultTheme = filterThemeUniqueGlobal(themeSystemDefault);
    const schemaTheme = isThemeSbem
        ? currentTheme || sbemShadCNThemeDefault
        : _.omit(sysDefaultTheme, 'link.color.normal');
    const themeSchemaFromLibrary = GenerateSchema.json(schemaTheme);
    const keysThatShouldBePresentButNotRequired = ['customContent', 'fontsUsage', 'page', 'page.favicon'];

    return getUpgradedJsonSchema(
        themeSchemaFromLibrary,
        'themeSchema',
        schemaTheme,
        keysThatShouldBePresentButNotRequired
    );
};

class EditTheme extends Component {
    constructor(props) {
        super(props);
        const urlParams = new URLSearchParams(props.location.search);
        const themeCategory = urlParams.get('category') || THEME_CATEGORY.SBE;
        this.state = {
            initialTheme: props.currentTheme,
            saveIsRequired: true,
            isFileValid: true,
            themeCategory
        };
    }

    componentDidMount() {
        if (this.state.initialTheme) {
            this.props.onFileChange(this.state.initialTheme, this.state.initialTheme, false);
        }

        this.props.setThemeCategory(this.state.themeCategory);
    }

    onChange = data => {
        this.props.onFileChange(this.state.initialTheme, data.value, data.valid);
        this.setState({ isFileValid: data.valid });
    };

    render() {
        const selectedTheme = (this.props.themeList || []).find(theme => theme.ContentCode === this.props.themeCode);
        const metadata = _.get(selectedTheme, 'MetaData');
        const { initialTheme, isFileValid } = this.state;
        const fileChanged = this.props.themeCode
            ? !_.isEqual(initialTheme, this.props.themeToSave)
            : !!this.props.themeToSave;
        const shouldLeavePageOnCancel = this.props.themeCode ? true : !this.props.themeToSave;
        const themeCode = this.props.themeCode;
        const currentValue = this.props.themeToSave;
        const themeSchema = createThemeSchema(this.props.isThemeSbem, this.props.currentTheme);

        return (
            <div>
                {themeCode || currentValue || currentValue === '' ? (
                    <div>
                        <div className="spark-panel spark-margin-top edit-JSON-form">
                            <div className="spark-panel__content">
                                <div className="title spark-mar-t-1">
                                    {themeCode ? (
                                        <h1>
                                            Edit - Theme: <span className="Strong">{themeCode}</span>
                                        </h1>
                                    ) : (
                                        <h1>Create - Theme</h1>
                                    )}
                                </div>
                                {metadata && <MetaData metadata={metadata} />}
                                <div className="json-code-editor-panel__wrapper">
                                    <div className="json-code-editor-panel">
                                        <div className="json-code-editor-panel__editor-wrap">
                                            {!isFileValid && <SchemaValidationErrorMessage />}
                                            {(currentValue || currentValue === '') && (
                                                <JsonCodeEditorPanel
                                                    onChange={this.onChange}
                                                    currentValue={currentValue}
                                                    schema={themeSchema}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <SaveAndCancelBottomButtons
                            level={this.props.level}
                            chainId={this.props.chainId}
                            hotelId={this.props.hotelId}
                            isSaveBtnDisabled={!this.props.themeFileChanged}
                            setFileData={this.props.setThemeFileData}
                            selected={this.props.selected}
                            code={this.props.themeCode}
                            list={this.props.themeList}
                            titleName="Theme"
                            fileToSave={this.props.themeToSave}
                            themeCategory={this.state.themeCategory}
                            onAfterSave={() => {
                                this.props.router.push('/manage/manage-theme');
                            }}
                            onCancelClick={() => {
                                this.props.router.push('/manage/manage-theme');
                            }}
                        />
                    </div>
                ) : (
                    <UploadForm
                        type="Theme"
                        level={this.props.level}
                        dropZoneDescription="A valid JSON theme file is expected. Download an existing theme file to determine parameters expected."
                        onLoad={data => {
                            this.props.onFileChange(initialTheme, data, false);
                            this.setState({
                                initialTheme: data
                            });
                        }}
                    />
                )}
                <OnLeaveSaveFileDialog
                    router={this.props.router}
                    route={this.props.route}
                    level={this.props.level}
                    chainId={this.props.chainId}
                    hotelId={this.props.hotelId}
                    onPageLeave={this.props.onPageLeave}
                    fileChanged={fileChanged}
                    shouldLeavePageOnCancel={shouldLeavePageOnCancel}
                    titleName="Theme"
                />
            </div>
        );
    }
}

EditTheme.propTypes = {
    chainId: PropTypes.string.isRequired,
    hotelId: PropTypes.string.isRequired,
    level: PropTypes.string.isRequired,
    themeList: PropTypes.array,
    currentTheme: PropTypes.object,
    themeToSave: PropTypes.object,
    saveThemeFile: PropTypes.func.isRequired,
    themeCode: PropTypes.string,
    themeFileChanged: PropTypes.boolean
};

const mapStateToProps = state => {
    const {
        chain,
        selectedHotel,
        level,
        themeList,
        themeToSave,
        code,
        currentTheme,
        themeFileChanged,
        isThemeSbem
    } = state.app.selected;
    return {
        selected: state.app.selected,
        level,
        chainId: chain.Id,
        hotelId: selectedHotel.id,
        themeToSave,
        themeCode: code,
        currentTheme: code ? currentTheme : null,
        themeFileChanged,
        isThemeSbem,
        themeList
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onCancelation: () => {
            dispatch({ type: ActionTypes.SET_THEME_FILE_READY_TO_SAVE, themeFileChanged: false });
            dispatch({ type: ActionTypes.SET_THEME_FILE_DATA, themeToSave: null });
        },
        onFileChange: (initialData, themeToSave, isCurrentDataValid) => {
            let themeFileChanged = false;
            if (isCurrentDataValid && !_.isEmpty(themeToSave)) {
                themeFileChanged = !_.isEqual(initialData, themeToSave);
            }

            dispatch({ type: ActionTypes.SET_THEME_FILE_READY_TO_SAVE, themeFileChanged });
            dispatch({ type: ActionTypes.SET_THEME_FILE_DATA, themeToSave });
        },
        onPageLeave: () => {
            dispatch({ type: ActionTypes.REFRESH_SELECTED_THEME_FILE });
        },
        setThemeFileData: (selected, params, oldCode, fileToSave, themeCategory) => {
            return dispatch(saveThemeFileAction(selected, params, oldCode, fileToSave, themeCategory)).then(() => {
                return true;
            });
        },
        setThemeCategory: (themeCategory) => {
            dispatch({ 
                type: ActionTypes.EDIT_STORE_CHANGED, 
                themeChange: false,
                configChange: false,
                themeChangeCategory: themeCategory 
            });
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EditTheme));



// WEBPACK FOOTER //
// ./src/components/pages/manage/editTheme/index.js